import type {
  VehicleReturnApi,
  VehicleReturnResult,
} from "@App/apis/graphql/vehicleReturn/interfaces";
import { apolloClient } from "@App/graphql/client";
import {
  CREATE_PICKUP_MUTATION,
  type CreatePickUpMutation,
} from "@App/graphql/mutations/returnVehicle/createPickup";
import {
  type ContractDetailsPickupQuery,
  GET_CONTRACT_DETAILS_PICKUP,
} from "@App/graphql/queries/returnVehicle/getContractDetailsPickup";
import type { ContractDetails } from "@App/types";
import { formatDate } from "date-fns";
import {
  ContractDetailsQuery,
  GET_CONTRACT_DETAILS,
} from "@App/graphql/queries/fleetManagement/getContractDetails";

export class VehicleReturnService implements VehicleReturnApi {
  async getContractDetailsPickUp({
    organizationId,
    payload,
  }: {
    organizationId: number;
    payload: { agreementNumber: string; numberPlate: string };
  }): Promise<VehicleReturnResult<ContractDetails>> {
    const { agreementNumber, numberPlate } = payload;
    const { data, error } = await apolloClient.query<ContractDetailsPickupQuery>({
      query: GET_CONTRACT_DETAILS_PICKUP,
      variables: {
        organization: organizationId,
        agreementNumber: agreementNumber,
        numberPlate: numberPlate,
      },
    });
    if (!!error || !data.organization.contractDetailsPickUp) {
      return {
        ok: false,
        error: error ?? new Error("Missing contract details"),
      };
    }
    return { ok: true, data: data.organization.contractDetailsPickUp };
  }

  async getContractDetails({
    organizationId,
    payload,
  }: {
    organizationId: number;
    payload: { agreementNumber: string; numberPlate: string };
  }): Promise<VehicleReturnResult<ContractDetails>> {
    const { agreementNumber, numberPlate } = payload;
    const { data, error } = await apolloClient.query<ContractDetailsQuery>({
      query: GET_CONTRACT_DETAILS,
      variables: {
        organization: organizationId,
        agreementNumber: agreementNumber,
        numberPlate: numberPlate,
      },
    });
    if (!!error || !data.organization.contractDetails) {
      return {
        ok: false,
        error: error ?? new Error("Missing contract details"),
      };
    }
    return { ok: true, data: data.organization.contractDetails };
  }

  async createVehiclePickup({
    organizationId,
    payload,
  }: {
    organizationId: number;
    payload: {
      carId: string;
      name: string;
      street: string;
      postcode: string;
      city: string;
      country: string;
      phone: string;
      email: string;
      customerAgreedToPaidPickup: boolean;
      dates: Date[];
    };
  }): Promise<VehicleReturnResult<boolean>> {
    const { dates, ...rest } = payload;
    const { data: createPickUp, errors } = await apolloClient.mutate<CreatePickUpMutation>({
      mutation: CREATE_PICKUP_MUTATION,
      variables: {
        organizationId,
        ...rest,
        returnData: {
          returnDates: dates.map((d) => formatDate(d, "yyy-MM-dd")),
        },
      },
    });
    if (!createPickUp || !!errors) {
      return {
        ok: false,
        error: new Error(errors.map((e) => e.message).join(", ")),
      };
    }
    return { ok: true, data: createPickUp.createPickUp };
  }
}
